import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import TemplateRenderer from "../components/TemplateRenderer";
import ShareBar from "../components/ShareBar";
import RsvpFloatingCta from "../components/RsvpFloatingCta";
import DemoBanner from "../components/DemoBanner";
import SEO from "../components/SEO";

export default function PublicInvitation() {
  const { slug } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/invitations/${slug}`)
      .then((r) => setInv(r.data))
      .catch(() => setError("Invitation not found"));
  }, [slug]);

  useEffect(() => {
    if (inv?.data?.groom_name && inv?.data?.bride_name) {
      document.title = `${inv.data.groom_name} weds ${inv.data.bride_name} · Weddinsite`;
    }
    return () => { document.title = "Weddinsite · Wedding Invitation Studio"; };
  }, [inv]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-stone-700">
      <div className="font-serif-display text-3xl mb-2">404</div>
      <div className="text-stone-500">This invitation does not exist.</div>
    </div>
  );
  if (!inv) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading...</div>;

  // Demo invitations (marked is_demo=True by backend via DEMO_SLUGS env var)
  // bypass the paid gate so they always render as a full, public, beautiful
  // website — perfect for ad landing pages and templates page previews.
  if (!inv.paid && !inv.is_demo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFBF7] text-stone-800 px-6 text-center" data-testid="invitation-pending-gate">
        <div className="text-[10px] uppercase tracking-[0.4em] text-stone-500 mb-4">Weddinsite</div>
        <div className="font-serif-display text-5xl md:text-6xl font-light mb-4" style={{ color: "#B89B5E" }}>
          {inv.data?.groom_name} & {inv.data?.bride_name}
        </div>
        <div className="font-serif-display text-2xl text-stone-600 italic mb-2">The invitation is being finalised.</div>
        <div className="text-stone-500 max-w-md">Please check back in a little while — the couple is putting the final touches to their wedding website.</div>
      </div>
    );
  }

  return (
    <div className="invitation-stationery pt-8 md:pt-9">
      <SEO
        title={inv.is_demo
          ? `${inv.data?.groom_name || ""} & ${inv.data?.bride_name || ""} Wedding · Live Demo`
          : `${inv.data?.groom_name || ""} & ${inv.data?.bride_name || ""} are getting married`}
        description={inv.is_demo
          ? `See a live demo of a Weddinsite wedding website — ${inv.data?.groom_name} & ${inv.data?.bride_name}. Build yours free in 30 seconds.`
          : `Join us in celebrating the wedding of ${inv.data?.groom_name} & ${inv.data?.bride_name}. View venue, events, story and RSVP online.`}
        path={`/i/${inv.slug}`}
        image={inv.data?.hero_image || inv.data?.cover_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": `Wedding of ${inv.data?.groom_name} & ${inv.data?.bride_name}`,
          "startDate": inv.data?.wedding_date,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": inv.data?.venue?.name || "Venue",
            "address": inv.data?.venue?.address || "India",
          },
          "image": [inv.data?.hero_image || inv.data?.cover_image].filter(Boolean),
          "organizer": {"@type": "Organization", "name": "Weddinsite", "url": "https://weddinsite.com"},
        }}
      />
      {inv.is_demo && <DemoBanner />}
      <ShareBar groomName={inv.data.groom_name} brideName={inv.data.bride_name} weddingDate={inv.data.wedding_date} slug={inv.slug} />
      <TemplateRenderer templateId={inv.template_id} data={inv.data} slug={inv.slug} />
      <RsvpFloatingCta accent={inv.data?.palette?.accent || "#B89B5E"} />
    </div>
  );
}
